import Helpers from '@/helpers/index.js';
import { useDate } from 'vuetify';

interface Option {
  label: string;
  value: string | null;
  color?: string;
  disable?: boolean;
  icon?: string;
  id?: number;
}

interface ChipColor {
  label: string;
  color: string;
}

interface GridRow {
  [key: string]: any;
}

interface Product {
  name?: string; // Optional since you are using optional chaining (`?.`)
  [key: string]: any; // To allow additional properties
}

interface FormattedProduct extends Product {
  chipName: string;
  isRemoved: boolean;
  isLoading: boolean;
}

interface Deliverable {
  id: string;
  name: string;
  status: string;
}

interface BriefingFile {
  name?: string; // Optional since you are using optional chaining (`?.`)
  [key: string]: any; // To allow additional properties
}

interface FormattedBriefingFile extends BriefingFile {
  chipName: string;
  isRemoved: boolean;
  isLoading: boolean;
}

export const PROJECT_DELIVERABLE_TRACKING_ENTITY_FIELDS = [
  {
    field: 'Tracking Status',
    entity: 'project_deliverable',
    type: 'string',
    options: [],
  },
  {
    field: 'Shipping Info Rec',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'Product Received',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'Concept Received',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'Concept Feedback',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'Revised Concept Received',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'First Draft Submission Date',
    entity: 'project_deliverable',
    type: 'datetime',
    options: [],
  },
  {
    field: 'First Draft Received',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'Content Feedback Shared',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'Final Content Revised Received',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
  {
    field: 'Content Approved',
    entity: 'project_deliverable',
    type: 'checkbox',
    options: [],
  },
];

export default {
  /**
   * Get the orientation (horizontal/vertical) of an image or video file
   * @param {File} file - The file to check orientation for
   * @returns {Promise<string>} Returns a promise that resolves to 'horizontal', 'vertical', or 'unknown'
   */
  getMediaOrientation(file: File): Promise {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          resolve(img.width >= img.height ? 'horizontal' : 'vertical');
        };
        img.src = URL.createObjectURL(file);
      } else if (file.type.startsWith('video/')) {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          resolve(video.videoWidth >= video.videoHeight ? 'horizontal' : 'vertical');
        };
        video.src = URL.createObjectURL(file);
      } else {
        resolve('unknown');
      }
    });
  },

  getEntityMappingFieldTypes(): Option[] {
    return [
      { label: 'Text', value: 'string', disable: false, icon: 'header-string' },
      { label: 'Number', value: 'integer', disable: false, icon: 'header-integer' },
      { label: 'Percent', value: 'percent', disable: false, icon: 'header-percentage' },
      { label: 'Decimal', value: 'float', disable: false, icon: 'header-integer' },
      { label: 'Currency', value: 'currency', disable: false, icon: 'header-currency' },
      { label: 'Checkbox', value: 'checkbox', disable: false, icon: 'header-checkbox' },
      { label: 'Date', value: 'datetime', disable: false, icon: 'header-datetime' },
      { label: 'URL', value: 'url', disable: false, icon: 'header-url' },
      { label: 'Multi-Select Dropdown', value: 'multi-select', disable: false, icon: 'header-multi-select' },
      { label: 'Single-Select Dropdown', value: 'single-select', disable: false, icon: 'header-dropdown' },
      { label: 'Single-Select Freeform', value: 'single-select-freeform', disable: false, icon: 'header-dropdown' },
      { label: 'Array', value: 'array', disable: true, icon: 'header-dropdown' },
      { label: 'Rating', value: 'rating', disable: true },
      // { label: 'Attachment', value: 'attachment', disable: false, icon: 'header-file' },
    ];
  },

  formatEntityMappingOption(option: string | { label?: string; value?: string }) {
    if (typeof option === 'object' && option !== null) {
      return {
        value: option.value ?? option.label ?? '',
        label: option.label ?? option.value ?? '',
      };
    }

    const optionString = String(option ?? '');
    const [value, ...labelParts] = optionString.split('::');

    if (labelParts.length === 0) {
      return { value: value, label: value };
    }

    return {
      value: value,
      label: labelParts.join('::'),
    };
  },

  serializeEntityMappingOption(option: string | { label?: string; value?: string }) {
    if (typeof option !== 'object' || option === null) {
      return String(option ?? '');
    }

    const value = String(option.value ?? option.label ?? '');
    const label = String(option.label ?? option.value ?? '');

    return label !== '' && label !== value ? `${value}::${label}` : value;
  },

  getProjectThemeColors(): { color: string }[] {
    return [
      { color: '#5271FF' },
      { color: '#004AAD' },
      { color: '#5DE1E6' },
      { color: '#0FC0DF' },
      { color: '#FDC619' },
      { color: '#7ED957' },
      { color: '#01BF62' },
      { color: '#353535' },
      { color: '#9A51E0' },
      { color: '#EB5756' },
    ];
  },

  getGenderOptions(): Option[] {
    return [
      { value: 'Male', label: 'Male', color: '#007bff' },
      { value: 'Female', label: 'Female', color: '#d2bbfe' },
      { value: 'Transgender', label: 'Transgender', color: '#f8312f' },
      { value: 'Non Binary', label: 'Non Binary', color: '#f8312f' },
      { value: 'Prefer_not_to_answer', label: 'Prefer not to answer', color: '#e6e6e6' },
    ];
  },

  getVerticalOptions(): Option[] {
    return [
      { label: 'Pets', value: 'Pets' },
      { label: 'Beauty', value: 'Beauty' },
      { label: 'Fashion', value: 'Fashion' },
      { label: 'Consumer Electronics', value: 'Consumer_Electronics' },
      { label: 'Health & Wellness', value: 'Health_&_Wellness' },
      { label: 'Outdoors', value: 'Outdoors' },
      { label: 'Kitchen/Cooking', value: 'Kitchen/Cooking' },
      { label: 'Gaming', value: 'Gaming' },
      { label: 'Parenting/Baby', value: 'Parenting/Baby' },
    ];
  },

  getEthnicityOptions(): Option[] {
    return [
      { value: 'White/Caucasian', label: 'White/Caucasian' },
      { value: 'Black/African American', label: 'Black/African American' },
      { value: 'Hispanic', label: 'Hispanic' },
      { value: 'Asian/Pacific Islander', label: 'Asian/Pacific Islander' },
      { value: 'Native American', label: 'Native American' },
      { value: 'Other', label: 'Other' },
      { value: 'Prefer not to answer', label: 'Prefer not to answer' },
    ];
  },

  getEducationOptions(): Option[] {
    return [
      { value: 'High School', label: 'High School' },
      { value: 'Some College', label: 'Some College' },
      { value: 'Bachelor', label: 'Bachelor' },
      { value: 'Master', label: 'Master' },
      { value: 'Doctorate', label: 'Doctorate' },
      { value: 'Prefer not to answer', label: 'Prefer not to answer' },
    ];
  },

  getLanguageOptions(): Option[] {
    return [
      { value: 'English', label: 'English' },
      { value: 'French', label: 'French' },
      { value: 'German', label: 'German' },
      { value: 'Spanish', label: 'Spanish' },
      { value: 'Japanese', label: 'Japanese' },
      { value: 'Other', label: 'Other' },
      { value: 'Prefer not to answer', label: 'Prefer not to answer' },
    ];
  },

  getRightsOptions(): Option[] {
    return [
      { label: 'Perpetual', value: 'Perpetual', id: 1 },
      { label: 'Passive Aft', value: 'Passive_Aft', id: 2 },
      { label: 'Limited To', value: 'Limited_To', id: 3 },
      { label: 'N/A', value: 'N/A', id: 4 },
    ];
  },

  getOwnershipOptions(): Option[] {
    return [
      { label: 'Client', value: 'Client' },
      { label: 'Influencer', value: 'Influencer' },
    ];
  },

  getPostingToSocialChannelsOptions(): Option[] {
    return [
      { label: 'Facebook', value: 'Facebook' },
      { label: 'Instagram', value: 'Instagram' },
      { label: 'Twitter', value: 'Twitter' },
      { label: 'YouTube', value: 'YouTube' },
      { label: 'TikTok', value: 'TikTok' },
      { label: 'Blog', value: 'Blog' },
      { label: 'Pinterest', value: 'Pinterest' },
      { label: 'LinkedIn', value: 'LinkedIn' },
      { label: 'Discord', value: 'Discord' },
      { label: 'Snapchat', value: 'Snapchat' },
      { label: 'Amazon Live', value: 'Amazon-Live' },
    ];
  },

  getClientStatusOptions(): Option[] {
    return [
      { label: 'Negotiating', value: 'Negotiating' },
      { label: 'Drafting Contract', value: 'Drafting_Contract' },
      { label: 'Out For Signature', value: 'Out_For_Signature' },
      { label: 'Redlines', value: 'Redlines' },
      { label: 'Expressed Interest', value: 'Expressed_Interest' },
      { label: 'Closed Lost', value: 'Closed_Lost' },
      { label: 'Signed', value: 'Signed' },
    ];
  },

  getDecisionOptions(): Option[] {
    return [
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
      { label: 'Maybe', value: 'Maybe' },
    ];
  },

  getPayByOptions(): Option[] {
    return [
      { label: 'ACH', value: 'ACH' },
      { label: 'PayPal', value: 'PayPal' },
      { label: 'Stripe', value: 'Stripe' },
      { label: 'Other', value: 'Other' }
    ];
  },

  getPayeeOptions(): Option[] {
    return [
      { label: 'Direct', value: 'Direct' },
      { label: 'Manager', value: 'Manager' },
      { label: 'Agent', value: 'Agent' }
    ];
  },

  getPaymentStatusOptions(): Option[] {
    return [
      { label: 'Pending Approval', value: '' },
      { label: 'Ready to Pay', value: 'Ready_to_Pay' },
      { label: 'Processing', value: 'Processing' },
      { label: 'Paid', value: 'Paid' },
    ];
  },

  getChipColors(): ChipColor[] {
    return [
      { label: 'Client', color: '#4a80ec' },
      { label: 'Influencer', color: '#4fe01c' },
      { label: 'Client (Expired)', color: '#ff8080' },
      { label: 'Negotiating', color: '#1f9ac8' },
      { label: 'Drafting Contract', color: '#d580ff' },
      { label: 'Out For Signature', color: '#e19a00' },
      { label: 'Redlines', color: '#ff8080' },
      { label: 'Expressed Interest', color: '#2e9652' },
      { label: 'Closed Lost', color: '#ff8080' },
      { label: 'Signed', color: '#3fce05' },
      { label: 'N/A', color: '#e6e6e6' },
      { label: 'Perpetual', color: '#ca1f63' },
      { label: 'Passive Aft', color: '#846534' },
      { label: 'Limited To', color: '#498098' },
      { label: 'White/Caucasian', color: '#537dd7' },
      { label: 'Black/African American', color: '#b17dea' },
      { label: 'Hispanic', color: '#007bff' },
      { label: 'Asian/Pacific Islander', color: '#d7174c' },
      { label: 'Native American', color: '#b26767' },
      { label: 'Other', color: '#a8867e' },
      { label: 'Prefer not to answer', color: '#6b5f5f' },
      { label: 'Male', color: '#007bff' },
      { label: 'Female', color: '#73bcd2' },
      { label: 'Transgender', color: '#d39f1c' },
      { label: 'Non Binary', color: '#d39f1c' },
      { label: 'Pending', color: '#537dd7' },
      { label: 'Live', color: '#008502' },
      { label: 'Removed', color: '#ff8080' },
      { label: 'Pre Production', color: '#7b027e' },
      { label: 'Production', color: '#0581ce' },
      { label: 'In Review', color: '#d39f1c' },
      { label: 'Clear To Publish', color: '#09910b' },
      { label: 'Approved', color: '#00a035' },
      { label: 'Feedback Provided', color: '#e19a00' },
      { label: 'Sent Back', color: '#e53f00' },
      { label: 'Approved With Changes', color: '#4fc29f' },
      { label: 'Error', color: '#ff8080' },
      { label: 'Delete', color: '#ff8080' },
      { label: 'Created', color: '#008502' },
      { label: 'Received', color: '#007bff' },
      { label: 'Pending Approval', color: '#007bff' },
      { label: 'Rejected', color: '#ff8080' },
      { label: 'Sent To Client', color: '#007bff' },
      { label: 'Agreement', color: '#ff8080' },
      { label: 'Invoice', color: '#00ae9b' },
      { label: 'W9', color: '#fd6b3a' },
      { label: 'NDA', color: '#4877c8' },
      { label: 'Other', color: '#007bff' },
      { label: 'ACH', color: '#00ae9b' },
      { label: 'PayPal', color: '#ffb300' },
      { label: 'Stripe', color: '#6772e5' },
      { label: 'Direct', color: '#00ae9b' },
      { label: 'Manager', color: '#6772e5' },
      { label: 'Agent', color: '#ffb300' },
      { label: 'Ready to Pay', color: '#fd6b3a' },
      { label: 'Processing', color: '#6772e5' },
      { label: 'Paid', color: '#00ae9b' },
    ];
  },

  getChipLabelColor(item: string): string {
    const colors = this.getChipColors();
    // check if item is matches any colors.label exists in the colors array.
    return colors.find((option) => item === option.label)?.color ?? 'black';
  },

  getDeliverableDestinationStatusOptions(): Option[] {
    return [
      { label: 'Pending', value: 'Pending' },
      { label: 'Live', value: 'Live' },
      { label: 'Removed', value: 'Removed' },
    ];
  },

  getDeliverableStatusOptions(): Option[] {
    return [
      { label: 'Pre Production', value: 'Pre-Production' },
      { label: 'Production', value: 'Production' },
      { label: 'In Review', value: 'In_Review' },
      { label: 'Rejected', value: 'Rejected' },
      { label: 'Feedback Provided', value: 'Feedback_Provided' },
      { label: 'Sent Back', value: 'Sent_Back' },
      { label: 'Sent to Client', value: 'Sent_To_Client' },
      { label: 'Approved', value: 'Approved' },
      { label: 'Approved With Changes', value: 'Approved_With_Changes' },
      { label: 'Clear to Publish', value: 'Clear_To_Publish' },
      { label: 'Live', value: 'Live' },
      //{ label: 'Exclude', value: 'Exclude' },
      //{ label: 'Delete', value: 'Delete' },
    ];
  },

  getDeliverableAssetStatusOptions(): Option[] {
    return [
      { label: 'Admin Only', value: 'Admin_Only' },
      { label: 'Approved for Client Review', value: 'Approved_for_Client_Review' },
      { label: 'Client Approved', value: 'Client_Approved' },
      { label: 'Clear To Publish', value: 'Clear_To_Publish' },
      { label: 'Live', value: 'Live' },
      { label: 'Sent Back', value: 'Sent_Back' },
      { label: 'Client Rejected', value: 'Client_Rejected' },
      { label: 'Feedback Provided', value: 'Feedback_Provided' },
      { label: 'Approved With Changes', value: 'Approve_With_Changes' },
    ];
  },

  getDocumentTypeOptions(): Option[] {
    return [
      { label: 'Agreement', value: 'Agreement' },
      { label: 'Invoice', value: 'Invoice' },
      { label: 'W9', value: 'W9' },
      { label: 'NDA', value: 'NDA' },
      { label: 'Other', value: 'Other' },
    ];
  },

  formatValueString(value: string | null | false): string {
    if (value === null || value === undefined || value === false) {
      return 'N/A';
    }
    switch (value?.toLowerCase()) {
      case 'male':
        return 'Male';
      case 'female':
        return 'Female';
      case 'transgender':
        return 'Transgender';
      case 'non_binary':
        return 'Non Binary';
      case 'prefer_not_to_answer':
        return 'Prefer not to answer';
      case 'pets':
        return 'Pets';
      case 'beauty':
        return 'Beauty';
      case 'fashion':
        return 'Fashion';
      case 'consumer_electronics':
        return 'Consumer Electronics';
      case 'health_&_wellness':
        return 'Health & Wellness';
      case 'outdoors':
        return 'Outdoors';
      case 'kitchen/cooking':
        return 'Kitchen/Cooking';
      case 'gaming':
        return 'Gaming';
      case 'parenting/baby':
        return 'Parenting/Baby';
      case 'white/caucasian':
        return 'White/Caucasian';
      case 'black/african american':
        return 'Black/African American';
      case 'hispanic':
        return 'Hispanic';
      case 'asian/pacific_islander':
        return 'Asian/Pacific Islander';
      case 'native_american':
        return 'Native American';
      case 'other':
        return 'Other';
      case 'perpetual':
        return 'Perpetual';
      case 'passive_aft':
        return 'Passive Aft';
      case 'limited_to':
        return 'Limited To';
      case 'client':
        return 'Client';
      case 'influencer':
        return 'Influencer';
      case 'drafting_contract':
        return 'Drafting Contract';
      case 'out_for_signature':
        return 'Out For Signature';
      case 'redlines':
        return 'Redlines';
      case 'expressed_interest':
        return 'Expressed Interest';
      case 'closed_lost':
        return 'Closed Lost';
      case 'signed':
        return 'Signed';
      case 'pre-production':
      case 'pre_production':
        return 'Pre Production';
      case 'clear_to_publish':
        return 'Clear To Publish';
      case 'in_review':
        return 'In Review';
      case 'feedback_provided':
        return 'Feedback Provided';
      case 'sent_back':
        return 'Sent Back';
      case 'approved_with_changes':
        return 'Approved With Changes';
      case 'sent_to_client':
        return 'Sent To Client';
      case 'agreement':
        return 'Agreement';
      case 'invoice':
        return 'Invoice';
      case 'w9':
        return 'W9';
      case 'nda':
        return 'NDA';
      default:
        return value;
    }
  },

  formatFilterTitle(filterKey: string): string {
    switch (filterKey) {
      case 'name_of_partner':
        return 'Name of Partner';
      case 'product_name':
        return 'Product Name';
      case 'destination_type':
        return 'Posting To';
      case 'status':
        return 'Status';
      case 'rights':
        return 'Rights';
      case 'asset_count':
        return 'Assets';
      case 'decisions':
        return 'Decisions';
      default:
        return filterKey;
    }
  },

  isValidJSON(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  },

  isNumberFields(): string[] {
    return [
      'savings.display_value',
      'contract_value.display_value',
      'initial_quote.display_value',
      'instagram_reach.display_value',
      'instagram_impressions.display_value',
      'instagram_engagement_rate.display_value',
      'tiktok_reach.display_value',
      'tiktok_impressions.display_value',
      'tiktok_engagement_rate.display_value',
      'youtube_reach.display_value',
      'youtube_impressions.display_value',
      'youtube_engagement_rate.display_value',
      'total_reach',
      'total_estimated_impressions',
    ];
  },

  mappingFieldNumberTypes(): string[] {
    return ['integer', 'float', 'rating', 'currency', 'percent', 'formula'];
  },

  hideFilterFields(): string[] {
    return ['savings.display_value', 'total_reach', 'total_estimated_impressions', 'documents.display_value'];
  },

  formatFilterValues(filterKey: string, filterValues: any[], customColumn: boolean, renderViewType: string): any[] {
    // console.log('formatFilterValues', filterKey, filterValues, customColumn, renderViewType);
    switch (filterKey) {
      case 'confirmed.display_value':
        return filterValues.map((value) => ({
          title: value,
          value: value,
        }));
      case 'initial_quote.display_value':
        return filterValues.map((value) => ({
          title: value ? `$${Helpers.formatNumberWithCommas(Number(value))}` : '$0',
          value: value,
        }));
      case 'contract_value.display_value':
        return filterValues.map((value) => ({
          title: value ? `$${Helpers.formatNumberWithCommas(Number(value))}` : '$0',
          value: value,
        }));
      case 'favorite.display_value':
        return filterValues.map((value) => ({
          title: value,
          value: value,
        }));
      case 'contract_status.display_value':
        return filterValues.map((value) => ({
          title: value ? this.formatValueString(value) : 'No Status',
          value: value,
        }));
      case 'savings.display_value':
        console.log('savings', filterValues);
        return filterValues.map((value) => ({
          title: value !== '$0' && value ? `$${Helpers.formatNumberWithCommas(Number(value))}` : '$0',
          value: value,
        }));
      case 'status.display_value':
        return filterValues.map((value) => ({
          title: value ? this.formatValueString(value) : 'No Status',
          value: value,
        }));
      case 'social_channels.display_value':
        return filterValues.map((value) => ({
          title: value ?? 'No Social Handles',
          value: value,
        }));
      case 'deliverables.display_value':
        return filterValues.map((value) => ({
          title: value,
          value: value,
        }));
      case 'products.display_value':
        return filterValues
          .filter((value) => value)
          .map((value) => ({
            title: value,
            value: value,
          }));
      case 'instagram_reach.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatNumberWithCommas(Number(value)) : 'No Reach',
          value: value,
        }));
      case 'instagram_impressions.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatNumberWithCommas(Number(value)) : 'No Impressions',
          value: value,
        }));
      case 'instagram_engagement_rate.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatPercentage(Number(value)) : 'No Engagement Rate',
          value: value,
        }));
      case 'tiktok_reach.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatNumberWithCommas(Number(value)) : 'No Reach',
          value: value,
        }));
      case 'tiktok_impressions.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatNumberWithCommas(Number(value)) : 'No Impressions',
          value: value,
        }));
      case 'tiktok_engagement_rate.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatPercentage(Number(value)) : 'No Engagement Rate',
          value: value,
        }));
      case 'youtube_reach.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatNumberWithCommas(Number(value)) : 'No Reach',
          value: value,
        }));
      case 'youtube_impressions.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatNumberWithCommas(Number(value)) : 'No Impressions',
          value: value,
        }));
      case 'youtube_engagement_rate.display_value':
        return filterValues.map((value) => ({
          title: value ? Helpers.formatPercentage(Number(value)) : 'No Engagement Rate',
          value: value,
        }));
      case 'verticals.display_value':
        return filterValues.map((value) => ({
          title: value ? this.formatValueString(value) : 'No Verticals',
          value: value,
        }));
      case 'content_rights.display_value':
        return filterValues.map((value) => ({
          title: value ? this.formatValueString(value) : 'No Rights',
          value: value,
        }));
      case 'notes.display_value':
        return filterValues.map((value) => ({
          title: value,
          value: value,
        }));
      case 'example_content.display_value':
        return filterValues.map((value) => ({
          title: value,
          value: value,
        }));
      default:
        return filterValues.map((value) => ({
          title: value,
          value: value,
        }));
    }
  },

  formatGroupRowValueString(valueType: string, value: any): string {
    switch (valueType) {
      case 'BooleanTextFieldViewer':
        return value ? 'Yes' : 'No';
      case 'CurrentFieldViewer':
        return `$${Helpers.formatNumberWithCommas(value)}`;
      case 'IntegerFieldEditor':
        return Helpers.formatNumberWithCommas(value);
      case 'ChipTextFieldViewer':
        return this.formatValueString(value);
      case 'DateTimeFieldViewer':
        const date = useDate();
        return date.format(value, 'keyboardDate');
      //   console.log('AssetsFieldViewer', value);
      //   return `${value} assets`;
      //   return value.length > 0 ? value.filter((asset) => asset.versions.length > 0) : '0';
      default:
        return value;
    }
  },

  getColumnIcon(headerIcon: string): string | boolean {
    switch (headerIcon) {
      case 'header-string':
        return '/icons/text-short.svg';
      case 'header-user':
        return '/icons/user.svg';
      case 'header-checkbox':
        return '/icons/checkbox.svg';
      case 'header-integer':
        return '/icons/number.svg';
      case 'header-formula':
        return '/icons/formula.svg';
      case 'header-dropdown':
      case 'header-single-select':
      case 'header-single-select-freeform':
      case 'header-array':
        return '/icons/dropdown.svg';
      case 'header-multi-select':
        return '/icons/multiselect.svg';
      case 'header-social-channels':
        return 'icons/social-channels.svg';
      case 'header-percentage':
      case 'header-percent':
        return '/icons/percentage.svg';
      case 'header-date':
      case 'header-datetime':
        return '/icons/calendar.svg';
      case 'header-currency':
        return '/icons/currency.svg';
      case 'header-web':
        return '/icons/web.svg';
      case 'header-url':
        return '/icons/link.svg';
      case 'header-favorite':
        return '/icons/heart.svg';
      case 'header-trash':
        return '/icons/trash.svg';
      case 'header-tiktok':
        return '/icons/social-tiktok.svg';
      case 'header-instagram':
        return '/icons/social-instagram.svg';
      case 'header-youtube':
        return '/icons/social-youtube.svg';
      case 'header-assets':
        return '/icons/assets.svg';
      case 'header-posting':
        return '/icons/posting-to.svg';
      case 'header-file':
        return '/icons/file.svg';
      default:
        return false;
    }
  },

  getFilterByDisplayNames(keys = {}, activeView: any, activeColumns: any) {
    const keyNames = [];
    for (const [key, value] of Object.entries(keys)) {
      keyNames.push(key);
    }

    if (activeView.display_mode === 'card') {
      const formattedNames = keyNames.map((key) => this.formatFilterTitle(key));
      return `${formattedNames.length >= 2 ? `${formattedNames.slice(0, 2).join(', ')}...` : formattedNames.join(', ')}`;
    }

    const names = activeColumns.filter((column) => keyNames.includes(column.field)).map((column) => column.headerName);
    return `${names.length >= 2 ? `${names.slice(0, 2).join(', ')}...` : names}`;
  },

  formatProducts(products: Product[] | null | undefined): FormattedProduct[] {
    if (!products || products.length === 0) {
      return [];
    }

    return products.map((product) => ({
      ...product,
      name: `${product.name?.substring(0, 40)}...`,
      chipName: `${product.name?.substring(0, 10)}...`,
      isRemoved: false,
      isLoading: false,
    }));
  },

  formatDeliverables(deliverables: Deliverable[]): { id: number; name: string; status: string }[] {
    return deliverables.map((deliverable, index) => ({
      id: Number(deliverable.id),
      name: deliverable.name ?? `Deliverable ${index + 1}`,
      status: deliverable.status,
      // creator: {
      //   id: Number(deliverable.creator.id),
      //   creator_id: Number(deliverable.creator.account_creator?.id),
      //   avatar: deliverable.creator.account_creator?.avatar,
      //   username: deliverable.creator.account_creator?.username
      // }
    }));
  },

  formatCellValueForExport(field: string, value: unknown, renderViewType?: string): unknown {
    const viewType = renderViewType ?? '';

    if (field === 'social_channels.display_value' || viewType === 'social_channels') {
      if (!Array.isArray(value)) {
        return value ?? '';
      }

      return value
        .map((channel: { handle?: string; url?: string } | null) => channel?.url ?? channel?.handle ?? '')
        .filter(Boolean)
        .join(', ');
    }

    if (field === 'deliverables.display_value' || viewType === 'deliverables') {
      if (!Array.isArray(value)) {
        return value ?? '';
      }

      return value
        .map((item: { name?: string } | string) => (typeof item === 'object' && item !== null ? item.name : item) ?? '')
        .filter(Boolean)
        .join(', ');
    }

    if (field === 'products.display_value' || viewType === 'products') {
      if (!Array.isArray(value)) {
        return value ?? '';
      }

      return value
        .map((item: { name?: string } | string) => (typeof item === 'object' && item !== null ? item.name : item) ?? '')
        .filter(Boolean)
        .join(', ');
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            const record = item as { name?: string; handle?: string; label?: string };
            return record.name ?? record.handle ?? record.label ?? '';
          }

          return item;
        })
        .filter(Boolean)
        .join(', ');
    }

    if (value && typeof value === 'object') {
      const record = value as { display_value?: unknown; name?: string; handle?: string; label?: string };
      if (record.display_value !== undefined) {
        return record.display_value;
      }

      return record.name ?? record.handle ?? record.label ?? '';
    }

    return value;
  },

  formatExampleContent(text: string): { url: string; label: string; image_url: string }[] {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let counter = 1;
    const results: { url: string; label: string; image_url: string }[] = [];

    text.replace(urlRegex, (url: string) => {
      // Remove trailing punctuation if present
      const cleanUrl = url.replace(/[.,;:!?]$/, '');
      const urlObject = new URL(cleanUrl);
      let hostname = urlObject.hostname;
      const parts = hostname.split('.');
      const channel = parts[parts.length - 2];
      const socialMediaIcon = Helpers.getSocialMediaIcon(channel);

      results.push({
        url: cleanUrl,
        label: `View Example ${counter}`,
        image_url: socialMediaIcon,
      });

      counter++;
      return url; // Add this line to satisfy the replace function's signature
    });

    return results;
  },

  formatBriefingFiles(briefingFiles: BriefingFile[] | null | undefined): FormattedBriefingFile[] {
    if (!briefingFiles || briefingFiles.length === 0) {
      return [];
    }

    return briefingFiles.map((briefingFile) => ({
      ...briefingFile,
      name: `${briefingFile.filename?.substring(0, 40)}...`,
      chipName: `${briefingFile.filename?.substring(0, 10)}...`,
      isRemoved: false,
      isLoading: false,
    }));
  },

  formatCustomColumn(columnData, columnSettings: { entityOrigin: string; isCreatorsEntity: boolean }, isCreatorDetailsViewActive: boolean) {
    if (!columnData) {
      return {};
    }
    // console.log('formatCustomColumn', columnData, columnSettings, isCreatorDetailsViewActive);
    let newColumnData = {};
    /*const isEditable = !(
      columnSettings?.entityOrigin === 'project_deliverables' ||
      (columnSettings?.entityOrigin === 'project_documents' && columnSettings?.isCreatorsEntity && columnData.entity === 'project_creator')
    );*/

    const allowsDeliverablesCreatorFieldEditing =
      columnSettings?.entityOrigin === 'project_deliverables' && columnData.entity === 'project_creator' && columnData.field === 'Usage Term';

    const isEditable = allowsDeliverablesCreatorFieldEditing || !(
      (columnSettings?.entityOrigin === 'project_deliverables' && columnSettings?.isCreatorsEntity && columnData.entity === 'project_creator') ||
      (columnSettings?.entityOrigin === 'project_documents' && columnSettings?.isCreatorsEntity && columnData.entity === 'project_creator')
    );

    const fieldName = `${columnData.field.replaceAll(' ', '').toLowerCase()}_custom_property`;
    const readOnlyCustomFields = ['Link to Reporting (URL)'];

    let filterParams = {
      defaultToNothingSelected: true,
      keyCreator: (params) => {
        return params.value;
      },
      valueFormatter: (params) => {
        return params.value;
      },
      values: ['Yes', 'No'],
    };

    if (columnData.type === 'integer' || columnData.type === 'currency' || columnData.type === 'percent') {
      filterParams = {
        defaultToNothingSelected: true,
        keyCreator: (params) => {
          const customProperties = params.data.customProperties;
          // get value from custom properties columnData.field matches a custom property key
          return customProperties ? customProperties.find((cp) => cp.key === columnData.field)?.value : 0;
        },
        valueFormatter: (params) => {
          const customProperties = params.data.customProperties;
          // get value from custom properties columnData.field matches a custom property key
          return customProperties ? customProperties.find((cp) => cp.key === columnData.field)?.value : '';
        },
      };
    }

    let filterValueGetter = (params) => {
      return params.data[fieldName].display_value ? 'Yes' : 'No';
    };

    if (columnData.type === 'integer' || columnData.type === 'currency') {
      filterValueGetter = (params) => {
        return params.data[fieldName].display_value ? Number(params.data[fieldName].display_value) : 0;
      };
    }

    if (columnData.type === 'percent') {
      filterValueGetter = (params) => {
        return params.data[fieldName].display_value ? Number(params.data[fieldName].display_value) : 0;
      };
    }

    newColumnData = {
      field: `${fieldName}.display_value`,
      headerName: columnData.field,
      headerClass: `header-${columnData?.type}`,
      sortable: true,
      editable: !isCreatorDetailsViewActive && readOnlyCustomFields.includes(columnData.field) ? false : isEditable,
      keyCreator: (params) => {
        return params.value;
      },
      // cellDataType: columnData.type === 'datetime' ? '' : this.mappingFieldNumberTypes().includes(columnData.type) ? 'number' : 'text',
      filter: this.mappingFieldNumberTypes().includes(columnData.type) ? 'agNumberColumnFilter' : true,
      filterParams: filterParams,
      filterValueGetter: filterValueGetter,
      cellEditorParams: {
        key: `${columnData.field.replaceAll(' ', '').toLowerCase()}_custom_property`,
        precision: 2,
        showStepperButtons: true,
      },
      valueFormatter: (params) => {
        const propertyValue = params.data?.customProperties ? params.data.customProperties.find((cp) => cp.key === columnData.field)?.value : null;
        let value = propertyValue ? (propertyValue !== 'null' && propertyValue !== 'null%' && propertyValue != 'Invalid Date' ? propertyValue : '') : null;
        switch (columnData.type) {
          case 'integer':
            return value ? Helpers.formatNumberWithCommas(Number(value)) : '';
          case 'percent':
            return value ? `${value}%` : '';
          case 'currency':
            return value ? `$${Helpers.formatNumberWithCommas(Number(value))}` : '';
          case 'datetime':
            return value ? Helpers.formatDate(value) : null;
          case 'string':
            return value !== null ? value : '';
          case 'multi-select':
            return value;
          default:
            return value;
        }
      },
      cellRenderer: (params) => {
        const value = params.value !== 'null' && params.value !== 'null%' && params.value != 'Invalid Date' ? params.value : null;
        switch (columnData.type) {
          case 'integer':
            return value ? Helpers.formatNumberWithCommas(Number(value)) : '';
          case 'percent':
            return value ? `${value}%` : '';
          case 'currency':
            return value ? `$${Helpers.formatNumberWithCommas(Number(value))}` : '';
          case 'datetime':
            return value ? Helpers.formatDate(value) : '';
          default:
            return value !== null ? value : '';
        }
      },
      // singleClickEdit: columnData.type === 'datetime',
      context: {
        entityName: columnData.entity,
        entityFieldName: columnData.field,
        entityMappingId: Number(columnData.id),
        entityFieldType: columnData.type,
        entityOptions: columnData.options,
        customColumn: true,
        filterType: this.mappingFieldNumberTypes().includes(columnData.type) ? 'number' : 'text',
        renderViewType: columnData.type === 'multi-select' ? 'chip' : columnData.type === 'single-select-freeform' ? 'string' : columnData.type,
        renderEditType: columnData.type,
        dropdownSelections: columnData.options.map((option) => this.formatEntityMappingOption(option)),
        payloadTransformer: (payload) => {
          if (columnData.type === 'multi-select') {
            return payload ? payload.map((option) => option).filter((option) => option !== '') : [];
          }
          return payload !== null ? String(payload) : '';
        },
      },
    };

    // check if columnData.type is not any of the following: 'integer', 'currency', 'percent', 'datetime', 'string'
    if (!['integer', 'currency', 'percent', 'datetime', 'string'].includes(columnData.type)) {
      delete newColumnData.cellRenderer;
    }

    // if (columnData.type === 'checkbox') {
    //   newColumnData.cellRenderer = 'agCheckboxCellRenderer';
    //   newColumnData.cellEditor = 'agCheckboxCellEditor';
    // }

    return newColumnData;
  },

  generateEmptyGridData(gridType: string, number: number, nextIndex: number): GridRow[] {
    const rows = [];
    for (let i = 0; i < number; i++) {
      if (gridType === 'creators') {
        rows.push({
          id: nextIndex++ + 1,
          creator_id: null,
          creator_account_id: null,
          avatar: {
            display_value: '',
          },
          first_name: {
            display_value: '',
          },
          last_name: {
            display_value: '',
          },
          creator: {
            display_value: '',
          },
          username: {
            display_value: '',
          },
          email: {
            display_value: '',
          },
          phone: {
            display_value: '',
          },
          gender: {
            display_value: '',
          },
          ethnicity: {
            display_value: '',
          },
          verticals: {
            display_value: '',
          },
          w9: {
            display_value: '',
          },
          w9_expiration_date: {
            display_value: '',
          },
          deliverables: {
            display_value: '',
          },
          products: {
            display_value: '',
          },
          social_channels: {
            display_value: '',
          },
          savings: {
            display_value: '',
          },
          indeminity: {
            display_value: '',
          },
          whitelisting: {
            display_value: '',
          },
          whitelisting_expiration: {
            display_value: '',
          },
          term_date: {
            display_value: '',
          },
          ecom_asset_ownership: {
            display_value: '',
          },
          content_rights_expiration_date: {
            display_value: '',
          },
          content_ownership: {
            display_value: '',
          },
          kill_fee: {
            display_value: '',
          },
          contract_notes: {
            display_value: '',
          },
          contract_value: {
            display_value: '',
          },
          contract_status: {
            display_value: '',
          },
          content_rights: {
            display_value: '',
          },
          favorite: {
            display_value: '',
          },
          confirmed: {
            display_value: '',
          },
          initial_quote: {
            display_value: '',
          },
          notes: {
            display_value: '',
          },
          decision: {
            display_value: '',
          },
          youtube_account: {
            display_value: '',
          },
          youtube_reach: {
            // find custom property value that matches the 'YT Reach' key from the custom properties array
            display_value: '',
          },
          youtube_impressions: {
            // find custom property value that matches the 'YT Impressions' key from the custom properties array
            display_value: '',
          },
          youtube_estimated_reach: {
            // find custom property value that matches the 'YT Estimated Reach' key from the custom properties array
            display_value: '',
          },
          tiktok_reach: {
            // find custom property value that matches the 'TikTok Reach' key from the custom properties array
            display_value: '',
          },
          tiktok_impressions: {
            // find custom property value that matches the 'TikTok Impressions' key from the custom properties array
            display_value: '',
          },
          tiktok_estimated_reach: {
            // find custom property value that matches the 'TikTok ER' key from the custom properties array
            display_value: '',
          },
          tiktok_account: {
            display_value: '',
          },
          instagram_reach: {
            // find custom property value that matches the 'Instagram Reach' key from the custom properties array
            display_value: '',
          },
          instagram_impressions: {
            // find custom property value that matches the 'IG Impressions' key from the custom properties array
            display_value: '',
          },
          instagram_estimated_reach: {
            // find custom property value that matches the 'IG ER' key from the custom properties array
            display_value: '',
          },
          instagram_account: {
            display_value: '',
          },
          example_content: {
            display_value: '',
          },
        });
      }

      if (gridType === 'deliverables') {
        rows.push({
          id: nextIndex++ + 1,
          asset_name: {
            display_value: '',
          },
          username: {
            display_value: '',
          },
          destinations: {
            display_value: '',
          },
          due_date: {
            display_value: '',
          },
          posts: {
            display_value: '',
          },
          production_status: {
            display_value: '',
          },
          product: {
            display_value: '',
          },
          notes: {
            display_value: '',
          },
          assets: {
            display_value: '',
          },
          status: {
            display_value: '',
          },
        });
      }
    }
    return rows;
  },

  getCustomColumnsForNewProject() {
    const projectCreatorEntity = [
      {
        field: 'Internal Notes',
        entity: 'project_creator',
        type: 'string',
        options: [],
      },
      {
        field: 'Mailing Address',
        entity: 'project_creator',
        type: 'string',
        options: [],
      },
      {
        field: 'Type',
        entity: 'project_creator',
        type: 'single-select',
        options: ['Nano', 'Micro', 'Mid Tier', 'Macro', 'Mega', 'Affiliate', 'Other'],
      },
      {
        field: 'Product Status',
        entity: 'project_creator',
        type: 'single-select',
        options: ['Locating Samples', 'Need Address', 'Ordered', 'Shipped', 'Received', 'Delays', 'customs'],
      },
      {
        field: 'Buyer',
        entity: 'project_creator',
        type: 'single-select',
        options: ['Creator', 'Client', 'We Are'],
      },
      {
        field: 'Shipper',
        entity: 'project_creator',
        type: 'single-select',
        options: ['FedEx', 'UPS', 'USPS', 'Other'],
      },
      {
        field: 'Usage Term',
        entity: 'project_creator',
        type: 'string',
        options: [],
      },
    ];

    const projectDeliverableEntity = [
      ...PROJECT_DELIVERABLE_TRACKING_ENTITY_FIELDS.map((field) => ({ ...field })),
      {
        field: 'Concept',
        entity: 'project_deliverable',
        type: 'url',
        options: [],
      },
      {
        field: 'Concept By',
        entity: 'project_deliverable',
        type: 'datetime',
        options: [],
      },
      {
        field: 'Pay Approved',
        entity: 'project_deliverable',
        type: 'datetime',
        options: [],
      },
      {
        field: 'Client Approval',
        entity: 'project_deliverable',
        type: 'datetime',
        options: [],
      },
      {
        field: 'Link to Reporting (URL)',
        entity: 'project_deliverable',
        type: 'url',
        options: [],
      },
      {
        field: 'Link to CTA (URL)',
        entity: 'project_deliverable',
        type: 'url',
        options: [],
      },
      {
        field: 'Tiktok Upload Links (URL)',
        entity: 'project_deliverable',
        type: 'url',
        options: [],
      },
      {
        field: 'Spark Code',
        entity: 'project_deliverable',
        type: 'string',
        options: [],
      },
    ];

    const projectDocumentEntity = [
      {
        field: 'Currency',
        entity: 'project_documents',
        type: 'single-select-freeform',
        options: [
          'USD::US Dollar (USD)',
          'EUR::Euro (EUR)',
          'JPY::Japanese Yen (JPY)',
          'GBP::British Pound Sterling (GBP)',
          'CNY::Chinese Yuan/Renminbi (CNY/CNH)',
          'AUD::Australian Dollar (AUD)',
          'CAD::Canadian Dollar (CAD)',
          'CHF::Swiss Franc (CHF)',
          'HKD::Hong Kong Dollar (HKD)',
          'SGD::Singapore Dollar (SGD)',
          'NZD::New Zealand Dollar (NZD)',
          'SEK::Swedish Krona (SEK)',
          'NOK::Norwegian Krone (NOK)',
          'KRW::South Korean Won (KRW)',
          'INR::Indian Rupee (INR)',
        ],
      },
      {
        field: 'Confirmed',
        entity: 'project_documents',
        type: 'checkbox',
        options: [],
      },
      {
        field: 'Notes',
        entity: 'project_documents',
        type: 'string',
        options: [],
      },
      /*{
        field: 'Submitted Date',
        entity: 'project_documents',
        type: 'datetime',
        options: [],
      },*/
      /*{
        field: 'Pay Status',
        entity: 'project_documents',
        type: 'checkbox',
        options: [],
      },*/
      {
        field: 'Approved Date',
        entity: 'project_documents',
        type: 'datetime',
        options: [],
      },
      {
        field: 'Approver Name',
        entity: 'project_documents',
        type: 'string',
        options: [],
      },
      /*{
        field: 'Paid Date',
        entity: 'project_documents',
        type: 'datetime',
        options: [],
      },*/
    ];

    return {
      project_creator_custom_columns: projectCreatorEntity,
      project_deliverable_custom_columns: projectDeliverableEntity,
      project_document_custom_columns: projectDocumentEntity,
    };
  },
};
